import React, { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { 
  MessageCircle, Send, X, ChevronLeft, User, Building2, 
  Clock, Check, CheckCheck, Loader2
} from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Get stored user from localStorage
const getStoredUser = () => {
  try {
    const user = localStorage.getItem('sfretire_user');
    return user ? JSON.parse(user) : null;
  } catch {
    return null;
  }
};

// Get user profile ID (handles different structures)
const getUserProfileId = (user) => {
  if (!user) return null;
  return user.profile?.id || user.id;
};

// Get user type
const getUserType = (user) => {
  if (!user) return null;
  return user.role || user.user_type || 'retraite';
};

const MessagingPanel = ({ isOpen, onClose }) => {
  const { t } = useTranslation();
  const [conversations, setConversations] = useState([]);
  const [selectedConversation, setSelectedConversation] = useState(null);
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState('');
  const [loading, setLoading] = useState(false);
  const [sending, setSending] = useState(false);
  const [unreadCount, setUnreadCount] = useState(0);
  const messagesEndRef = useRef(null);
  const user = getStoredUser();
  const userProfileId = getUserProfileId(user);
  const userType = getUserType(user);

  // Fetch conversations
  useEffect(() => {
    if (isOpen && user) {
      fetchConversations();
      fetchUnreadCount();
    }
  }, [isOpen, userProfileId]);

  // Scroll to bottom when messages change
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  const fetchConversations = async () => {
    if (!userProfileId) return;
    
    setLoading(true);
    try {
      const response = await fetch(
        `${API_URL}/api/conversations`,
        {
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('sfretire_token')}`
          }
        }
      );
      
      if (response.ok) {
        const data = await response.json();
        setConversations(data.items || []);
      }
    } catch (error) {
      console.error('Failed to fetch conversations:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchUnreadCount = async () => {
    if (!userProfileId) return;
    
    try {
      const response = await fetch(
        `${API_URL}/api/messages/unread`,
        {
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('sfretire_token')}`
          }
        }
      );
      
      if (response.ok) {
        const data = await response.json();
        setUnreadCount(data.unread_count || 0);
      }
    } catch (error) {
      console.error('Failed to fetch unread count:', error);
    }
  };

  const selectConversation = async (conversation) => {
    setSelectedConversation(conversation);
    setLoading(true);
    
    try {
      // Fetch messages
      const response = await fetch(
        `${API_URL}/api/conversations/${conversation.id}/messages`,
        {
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('sfretire_token')}`
          }
        }
      );
      
      if (response.ok) {
        const data = await response.json();
        setMessages(data.items || []);
      }
      
      // Mark as read
      await fetch(
        `${API_URL}/api/conversations/${conversation.id}/read?user_type=${userType}`,
        {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('sfretire_token')}`
          }
        }
      );
      
      fetchUnreadCount();
    } catch (error) {
      console.error('Failed to fetch messages:', error);
    } finally {
      setLoading(false);
    }
  };

  const sendMessage = async (e) => {
    e.preventDefault();
    if (!newMessage.trim() || !selectedConversation || sending) return;
    
    setSending(true);
    
    try {
      const response = await fetch(
        `${API_URL}/api/conversations/${selectedConversation.id}/messages`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${localStorage.getItem('sfretire_token')}`
          },
          body: JSON.stringify({
            content: newMessage,
            sender_id: userProfileId,
            sender_type: userType
          })
        }
      );
      
      if (response.ok) {
        const data = await response.json();
        setMessages(prev => [...prev, data.message]);
        setNewMessage('');
      } else {
        toast.error('Erreur lors de l\'envoi du message');
      }
    } catch (error) {
      console.error('Failed to send message:', error);
      toast.error('Erreur de connexion');
    } finally {
      setSending(false);
    }
  };

  const formatTime = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    const now = new Date();
    const diffDays = Math.floor((now - date) / (1000 * 60 * 60 * 24));
    
    if (diffDays === 0) {
      return date.toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' });
    } else if (diffDays === 1) {
      return 'Hier';
    } else if (diffDays < 7) {
      return date.toLocaleDateString('fr-FR', { weekday: 'short' });
    } else {
      return date.toLocaleDateString('fr-FR', { day: 'numeric', month: 'short' });
    }
  };

  if (!user) {
    return null;
  }

  return (
    <AnimatePresence>
      {isOpen && (
        <motion.div
          initial={{ opacity: 0, x: 400 }}
          animate={{ opacity: 1, x: 0 }}
          exit={{ opacity: 0, x: 400 }}
          transition={{ type: 'spring', damping: 25, stiffness: 300 }}
          className="fixed right-0 top-0 h-full w-full sm:w-[420px] bg-card border-l border-border shadow-elevated z-50 flex flex-col"
          data-testid="messaging-panel"
        >
          {/* Header */}
          <div className="flex items-center justify-between p-4 border-b border-border bg-card">
            {selectedConversation ? (
              <>
                <button
                  onClick={() => setSelectedConversation(null)}
                  className="p-2 hover:bg-secondary rounded-lg transition-colors"
                >
                  <ChevronLeft size={20} />
                </button>
                <div className="flex-1 ml-2">
                  <h3 className="font-semibold text-foreground">
                    {selectedConversation.other_party?.name || 'Conversation'}
                  </h3>
                  <p className="text-xs text-muted-foreground">
                    {selectedConversation.other_party?.type === 'entreprise' ? 'Entreprise' : 'Retraité'}
                  </p>
                </div>
              </>
            ) : (
              <>
                <div className="flex items-center gap-2">
                  <MessageCircle className="text-primary" size={24} />
                  <h3 className="font-semibold text-foreground text-lg">Messages</h3>
                  {unreadCount > 0 && (
                    <span className="bg-primary text-primary-foreground text-xs px-2 py-0.5 rounded-full">
                      {unreadCount}
                    </span>
                  )}
                </div>
              </>
            )}
            <button
              onClick={onClose}
              className="p-2 hover:bg-secondary rounded-lg transition-colors"
              data-testid="close-messaging"
            >
              <X size={20} />
            </button>
          </div>

          {/* Content */}
          <div className="flex-1 overflow-hidden">
            {loading ? (
              <div className="flex items-center justify-center h-full">
                <Loader2 className="animate-spin text-primary" size={32} />
              </div>
            ) : selectedConversation ? (
              // Messages View
              <div className="flex flex-col h-full">
                <div className="flex-1 overflow-y-auto p-4 space-y-3">
                  {messages.length === 0 ? (
                    <div className="text-center text-muted-foreground py-8">
                      <MessageCircle className="mx-auto mb-2 opacity-50" size={40} />
                      <p>Aucun message pour l&apos;instant</p>
                      <p className="text-sm">Commencez la conversation !</p>
                    </div>
                  ) : (
                    messages.map((msg) => (
                      <motion.div
                        key={msg.id}
                        initial={{ opacity: 0, y: 10 }}
                        animate={{ opacity: 1, y: 0 }}
                        className={`flex ${msg.sender_id === userProfileId ? 'justify-end' : 'justify-start'}`}
                      >
                        <div
                          className={`max-w-[80%] rounded-2xl px-4 py-2 ${
                            msg.sender_id === userProfileId
                              ? 'bg-primary text-primary-foreground rounded-br-md'
                              : 'bg-secondary text-foreground rounded-bl-md'
                          }`}
                        >
                          <p className="text-sm whitespace-pre-wrap">{msg.content}</p>
                          <div className={`flex items-center gap-1 mt-1 ${
                            msg.sender_id === userProfileId ? 'justify-end' : 'justify-start'
                          }`}>
                            <span className="text-xs opacity-70">
                              {formatTime(msg.created_at)}
                            </span>
                            {msg.sender_id === userProfileId && (
                              msg.read ? 
                                <CheckCheck size={12} className="opacity-70" /> : 
                                <Check size={12} className="opacity-70" />
                            )}
                          </div>
                        </div>
                      </motion.div>
                    ))
                  )}
                  <div ref={messagesEndRef} />
                </div>

                {/* Message Input */}
                <form onSubmit={sendMessage} className="p-4 border-t border-border bg-card">
                  <div className="flex gap-2">
                    <Input
                      value={newMessage}
                      onChange={(e) => setNewMessage(e.target.value)}
                      placeholder="Écrivez votre message..."
                      className="flex-1"
                      disabled={sending}
                      data-testid="message-input"
                    />
                    <Button 
                      type="submit" 
                      variant="gold" 
                      size="icon"
                      disabled={!newMessage.trim() || sending}
                      data-testid="send-message-btn"
                    >
                      {sending ? (
                        <Loader2 className="animate-spin" size={18} />
                      ) : (
                        <Send size={18} />
                      )}
                    </Button>
                  </div>
                </form>
              </div>
            ) : (
              // Conversations List
              <div className="overflow-y-auto h-full">
                {conversations.length === 0 ? (
                  <div className="text-center text-muted-foreground py-12 px-4">
                    <MessageCircle className="mx-auto mb-3 opacity-50" size={48} />
                    <p className="font-medium">Aucune conversation</p>
                    <p className="text-sm mt-1">
                      Vos conversations avec les {userType === 'retraite' ? 'entreprises' : 'retraités'} apparaîtront ici
                    </p>
                  </div>
                ) : (
                  <div className="divide-y divide-border">
                    {conversations.map((conv) => (
                      <motion.button
                        key={conv.id}
                        onClick={() => selectConversation(conv)}
                        className="w-full p-4 text-left hover:bg-secondary/50 transition-colors flex items-start gap-3"
                        whileHover={{ backgroundColor: 'rgba(0,0,0,0.02)' }}
                        data-testid={`conversation-${conv.id}`}
                      >
                        <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0">
                          {conv.other_party?.type === 'entreprise' ? (
                            <Building2 size={20} className="text-primary" />
                          ) : (
                            <User size={20} className="text-primary" />
                          )}
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center justify-between">
                            <h4 className="font-medium text-foreground truncate">
                              {conv.other_party?.name || 'Conversation'}
                            </h4>
                            <span className="text-xs text-muted-foreground flex-shrink-0 ml-2">
                              {formatTime(conv.last_message_at)}
                            </span>
                          </div>
                          <p className="text-sm text-muted-foreground truncate mt-0.5">
                            {conv.last_message_preview || 'Nouvelle conversation'}
                          </p>
                        </div>
                        {(userType === 'retraite' ? conv.unread_retraite : conv.unread_entreprise) > 0 && (
                          <span className="bg-primary text-primary-foreground text-xs w-5 h-5 rounded-full flex items-center justify-center flex-shrink-0">
                            {userType === 'retraite' ? conv.unread_retraite : conv.unread_entreprise}
                          </span>
                        )}
                      </motion.button>
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

// Floating Message Button Component
export const MessagingButton = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [unreadCount, setUnreadCount] = useState(0);
  const user = getStoredUser();

  const userId = user?.profile?.id || user?.id;
  const userRole = user?.role || user?.user_type || 'retraite';

  useEffect(() => {
    if (userId) {
      fetchUnreadCount();
      // Poll for new messages every 30 seconds
      const interval = setInterval(fetchUnreadCount, 30000);
      return () => clearInterval(interval);
    }
  }, [userId]);

  const fetchUnreadCount = async () => {
    if (!userId) return;
    
    try {
      const response = await fetch(
        `${API_URL}/api/messages/unread`,
        {
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('sfretire_token')}`
          }
        }
      );
      
      if (response.ok) {
        const data = await response.json();
        setUnreadCount(data.unread_count || 0);
      }
    } catch (error) {
      console.error('Failed to fetch unread count:', error);
    }
  };

  if (!user) {
    return null;
  }

  return (
    <>
      <motion.button
        onClick={() => setIsOpen(true)}
        className="fixed bottom-24 right-6 w-14 h-14 bg-primary text-primary-foreground rounded-full shadow-lg flex items-center justify-center hover:bg-primary/90 transition-colors z-40"
        whileHover={{ scale: 1.05 }}
        whileTap={{ scale: 0.95 }}
        data-testid="open-messaging-btn"
      >
        <MessageCircle size={24} />
        {unreadCount > 0 && (
          <span className="absolute -top-1 -right-1 bg-destructive text-destructive-foreground text-xs w-5 h-5 rounded-full flex items-center justify-center">
            {unreadCount > 9 ? '9+' : unreadCount}
          </span>
        )}
      </motion.button>

      <MessagingPanel isOpen={isOpen} onClose={() => setIsOpen(false)} />
      
      {/* Backdrop */}
      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={() => setIsOpen(false)}
            className="fixed inset-0 bg-black/20 z-40 sm:hidden"
          />
        )}
      </AnimatePresence>
    </>
  );
};

export default MessagingPanel;
